import type {
  RawVariationAttributeMapping,
  RawEnrichedVariationAttribute,
} from "../../../../../../types";
import type {
  VariationAttributeMapping,
  VariationAttributeCombination,
  EnrichedAttributeTermTransformed,
} from "../../../types/productApiTypes";

/**
 * A pure helper function to transform the raw variation attribute mappings data from the API.
 * This is crucial for linking selected attributes to a specific variation ID.
 * It also handles the translation of attribute and term names within the mapping.
 *
 * @param rawMappings The `items` array from the `variation_attribute_mappings` extension.
 * @param shouldTranslate A boolean indicating if translations should be applied.
 * @returns A new array of transformed variation mapping objects.
 */
export const transformVariationMappings = (
  rawMappings: RawVariationAttributeMapping[] | undefined,
  shouldTranslate: boolean
): VariationAttributeMapping[] => {
  if (!Array.isArray(rawMappings)) {
    return [];
  }

  return rawMappings.map(
    (rawMapping: RawVariationAttributeMapping): VariationAttributeMapping => {
      const transformedAttributes: VariationAttributeCombination[] = (
        rawMapping.attributes || []
      ).map(
        (
          varAttr: RawEnrichedVariationAttribute
        ): VariationAttributeCombination => {
          const transformedTerm: EnrichedAttributeTermTransformed | undefined =
            varAttr.term
              ? {
                  id: varAttr.term.id,
                  name:
                    shouldTranslate && varAttr.term.translated_name
                      ? varAttr.term.translated_name
                      : varAttr.term.name,
                  slug: varAttr.term.slug,
                  type: varAttr.term.type,
                  buttonColor: varAttr.term["button-color"],
                  buttonText:
                    shouldTranslate && varAttr.term.translated_name
                      ? varAttr.term.translated_name
                      : varAttr.term.name,
                  buttonImage: varAttr.term["button-image"],
                }
              : undefined;

          return {
            id: varAttr.id,
            name:
              shouldTranslate && varAttr.translated_name
                ? varAttr.translated_name
                : varAttr.name,
            value: varAttr.value,
            taxonomy: varAttr.taxonomy,
            hasVariations: varAttr.has_variations,
            displayType: varAttr.display_type,
            term: transformedTerm,
          };
        }
      );
      return {
        id: rawMapping.id,
        attributes: transformedAttributes,
      };
    }
  );
};
